import { useEffect, useState } from 'react';
import { Users } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { formatAmount, getIcon, catColor, catTint } from '../lib/constants';
import { getGoalInvite, joinGoalInvite } from '../lib/api';
import { friendlyError } from '../lib/errorReporting';

// Shown once a signed-in, onboarded user arrives with a pending
// ?goal_invite=CODE (see lib/attribution.js). Invitees join free — the
// goal owner's Pro covers them (enforced server-side).
export default function GoalInviteModal({ code, onDone }) {
  const [invite, setInvite] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getGoalInvite(code)
      .then(inv => {
        if (inv.already_member) onDone(inv.id);
        else setInvite(inv);
      })
      .catch(e => {
        toast.error(friendlyError(e, 'Esta invitación ya no es válida'));
        onDone(null);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  if (!invite) return null;
  const Icon = getIcon(invite.icon);
  const per = { daily: 'diarios', weekly: 'semanales', biweekly: 'quincenales', monthly: 'mensuales' }[invite.period];

  const join = async () => {
    setBusy(true);
    try {
      const goal = await joinGoalInvite(code);
      toast.success(`Te uniste a "${goal.name}"`);
      onDone(goal.id);
    } catch (e) {
      toast.error(friendlyError(e, 'No se pudo unir a la meta'));
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onDone(null); }}>
      <DialogContent className="bg-slate-900 border-white/10 text-slate-200 rounded-2xl max-w-sm" data-testid="goal-invite-modal">
        <div className="flex flex-col items-center text-center pt-2">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: catTint(invite.color) }}>
            <Icon className="w-6 h-6" style={{ color: catColor(invite.color) }} />
          </div>
          <DialogTitle className="text-lg font-semibold text-white">{invite.owner_name} te invitó a ahorrar juntos</DialogTitle>
          <DialogDescription className="text-sm text-slate-400 mt-1.5">
            Meta <span className="text-slate-200 font-medium">"{invite.name}"</span> de {formatAmount(invite.target_amount, false)}
            {per ? `, con aportes ${per}` : ''}.
          </DialogDescription>
          <p className="text-xs text-slate-500 mt-3 flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5" /> {invite.member_count} {invite.member_count === 1 ? 'persona' : 'personas'} · verán tus aportes a esta meta, nada más
          </p>
          <div className="flex flex-col gap-2 w-full mt-6">
            <Button onClick={join} disabled={busy} data-testid="goal-invite-join-btn"
              className="bg-violet-500 hover:bg-violet-600 text-on-brand rounded-full h-11 font-semibold">
              {busy ? 'Uniéndote…' : 'Unirme a la meta'}
            </Button>
            <button type="button" onClick={() => onDone(null)} className="hit-44 h-10 text-sm text-slate-400 hover:text-white">Ahora no</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
